import { OpeningHours, TimeSlot } from "api-types";
import { Hint, StyledButton } from "igfd-ui";

import React, { useCallback, useEffect } from "react";

import {
  Box,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { RemoveIcon } from "@dashboard/components/common/actionIcons";
import {
  AddCircleIcon,
  DuplicateIcon,
} from "@dashboard/components/common/icons";

import { daysOfWeek } from "./OpeningHoursConstants";

/**
 * Note this component uses local state stored inside the component.
 *
 * In case of contextual changes, and the need for rerendering, specify the key
 * prop to force a rerender.
 *
 * See https://www.saltycrane.com/blog/2019/12/how-remount-react-component-when-prop-changes/
 */
type OpeningHoursProps = {
  setValidity: (isValid: boolean) => void;
  openingHours: OpeningHours[];
  setOpeningHours: (openingHours: OpeningHours[]) => void;
};

const getFirstChar = (string: string) => {
  return string[0];
};

// this allows for restaurants to be open past midnight
// we assume the day changes at 4:30am
const epochHour = 4;
const epochMinute = 30;
const startOfDayOffsetMinutes = epochHour * 60 + epochMinute;
// initial interval when a new timeslot is added, to make it easier for the user to add more timeslots
const initialTimeSlot = () =>
  ({ startHour: 11, startMin: 0, endHour: 22, endMin: 0 } as TimeSlot);

const offsetMinute = (hour: number, minute: number) => {
  const offSet = hour * 60 + minute - startOfDayOffsetMinutes;
  return offSet >= 0 ? offSet : 24 * 60 + offSet;
};

const isValidTimeRange = (timeSlot: TimeSlot) => {
  const startOffsetMinutes = offsetMinute(
    timeSlot.startHour,
    timeSlot.startMin
  );
  const endOffsetMinutes = offsetMinute(timeSlot.endHour, timeSlot.endMin);

  if (startOffsetMinutes > endOffsetMinutes) {
    return false;
  }

  return true;
};

const overlappedTimeSlots = (timeSlots: TimeSlot[]) => {
  timeSlots.sort((t1, t2) => {
    const t1OffsetMinutes = offsetMinute(t1.startHour, t1.startMin);
    const t2OffsetMinutes = offsetMinute(t2.startHour, t2.startMin);
    return t1OffsetMinutes - t2OffsetMinutes;
  });

  for (let index = 1; index < timeSlots.length; index++) {
    const t1 = timeSlots[index - 1];
    const t2 = timeSlots[index];
    const t1EndTime = offsetMinute(t1.endHour, t1.endMin);
    const t2StartTime = offsetMinute(t2.startHour, t2.startMin);
    if (t1EndTime > t2StartTime) return true;
  }

  return false;
};

// Returns a boolean indicating if the given set of opening hours is valid.
export const isOpeningHoursValid = (openingHours: OpeningHours[]) => {
  // ensure that all time slots are valid
  const isTimeRangeValid = openingHours.every((openingHour) =>
    openingHour.timeSlots.every((timeSlot) => isValidTimeRange(timeSlot))
  );
  // check if there are any overlapped time slots
  const hasOverlappedTimeSlots = openingHours.some((openingHour) =>
    overlappedTimeSlots(openingHour.timeSlots)
  );

  return isTimeRangeValid && !hasOverlappedTimeSlots;
};

/**
 * Note this component uses local state stored inside the component.
 *
 * In case of contextual changes, and the need for rerendering, specify the key
 * prop to force a rerender.
 *
 * See https://www.saltycrane.com/blog/2019/12/how-remount-react-component-when-prop-changes/
 */
export const OpeningHoursInputComponent = ({
  setValidity,
  openingHours,
  setOpeningHours,
}: OpeningHoursProps) => {
  // assumption, one OpeningHours is for 1 day of the week only.
  // This assumption is a carry over from the angular & mobile app's interpretation of opening hours
  const [timeSlotsByIsoDayOfWeek, setTimeSlotsByIsoDayOfWeek] = React.useState<
    Map<number, TimeSlot[]>
  >(new Map(openingHours.map((day) => [day.isoDayOfWeeks[0], day.timeSlots])));

  useEffect(() => {
    const newOpeningHours: OpeningHours[] = Array.from(
      timeSlotsByIsoDayOfWeek.entries()
    ).map(([isoDayOfWeek, timeSlots]) => ({
      isoDayOfWeeks: [isoDayOfWeek],
      timeSlots,
    }));

    const isValid = isOpeningHoursValid(newOpeningHours);

    if (isValid) {
      setOpeningHours(newOpeningHours);
    }

    setValidity(isValid);
  }, [timeSlotsByIsoDayOfWeek, setValidity, setOpeningHours]);

  // if there is at least one time slot set for the day.
  const isDaySet = (isoDayOfWeek: number) => {
    const timeSlots = timeSlotsByIsoDayOfWeek.get(isoDayOfWeek);
    return timeSlots && timeSlots.length > 0;
  };

  // set or remove time slot for the day
  const toggleDay = useCallback(
    (isoDayOfWeek: number) => {
      const timeSlots = timeSlotsByIsoDayOfWeek.get(isoDayOfWeek);
      if (timeSlots && timeSlots.length > 0) {
        timeSlotsByIsoDayOfWeek.set(isoDayOfWeek, []);
      } else {
        timeSlotsByIsoDayOfWeek.set(isoDayOfWeek, [initialTimeSlot()]);
      }
      setTimeSlotsByIsoDayOfWeek(new Map(timeSlotsByIsoDayOfWeek));
    },
    [timeSlotsByIsoDayOfWeek]
  );

  // Copies the the slots form the first active day to all other active days
  const copySlotsForAllActiveDays = useCallback(() => {
    const activeDays = daysOfWeek.filter(({ value }) => isDaySet(value));
    const firstActiveDay = activeDays[0];
    const timeSlots = timeSlotsByIsoDayOfWeek.get(firstActiveDay.value);
    if (!timeSlots) {
      return;
    }
    activeDays.forEach((day) => {
      if (day.value !== firstActiveDay.value) {
        timeSlotsByIsoDayOfWeek.set(day.value, timeSlots);
      }
    });
    setTimeSlotsByIsoDayOfWeek(new Map(timeSlotsByIsoDayOfWeek));
  }, [timeSlotsByIsoDayOfWeek]);

  return (
    <Stack
      sx={({ palette }) => ({
        padding: "32px",
        gap: 3,
        display: "flex",
        borderRadius: "16px",
        backgroundColor: palette.grey.A100,
        border: `1px solid ${palette.grey.A200}`,
      })}
    >
      <OpeningHoursRow>
        {daysOfWeek.map((element) => {
          return (
            <StyledButton
              key={element.value}
              sx={{
                borderRadius: "8px",
                width: "40px",
                minWidth: "unset",
              }}
              label={getFirstChar(element.label)}
              variant={isDaySet(element.value) ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                toggleDay(element.value);
              }}
            />
          );
        })}
      </OpeningHoursRow>
      <Grid container gap={2.5}>
        {daysOfWeek
          .filter(({ value }) => isDaySet(value))
          .map((element, index) => (
            <Grid key={index} container sx={{ alignItems: "center" }}>
              <TimeSlotCell key={element.label} item xs={3}>
                {element.label}
              </TimeSlotCell>
              <TimeSlotCell
                key={element.value}
                item
                xs={9}
                sx={{ textAlign: "left" }}
              >
                <Times
                  showCopy={index === 0}
                  onCopyClick={copySlotsForAllActiveDays}
                  timeSlots={timeSlotsByIsoDayOfWeek.get(element.value)!}
                  setTimeSlots={(timeSlots) => {
                    timeSlotsByIsoDayOfWeek.set(element.value, timeSlots);
                    setTimeSlotsByIsoDayOfWeek(
                      new Map(timeSlotsByIsoDayOfWeek)
                    );
                  }}
                />
              </TimeSlotCell>
              {overlappedTimeSlots([
                ...timeSlotsByIsoDayOfWeek.get(element.value)!,
              ]) && (
                <Hint
                  sx={{
                    marginTop: 0.5,
                    width: "100%",
                  }}
                  hintValue={`Time Ranges cannot overlap.`}
                  severity="error"
                />
              )}
            </Grid>
          ))}
      </Grid>
    </Stack>
  );
};

const Times = ({
  showCopy,
  onCopyClick,
  timeSlots,
  setTimeSlots,
}: {
  showCopy: boolean;
  onCopyClick: () => void;
  timeSlots: TimeSlot[];
  setTimeSlots: (timeSlots: TimeSlot[]) => void;
}) => (
  <OpeningHoursRow>
    <Grid container gap={0.5}>
      {timeSlots.map((timeSlot, index) => {
        return (
          <Grid key={index} container sx={{ alignItems: "center" }}>
            <TimeSlotCell item xs={4}>
              <TimeSlotDisplayTimeStamp
                hour={timeSlot.startHour}
                minutes={timeSlot.startMin}
                onChange={(hour, minutes) => {
                  const newTimeSlots = [...timeSlots];
                  newTimeSlots[index] = {
                    startHour: hour,
                    startMin: minutes,
                    endHour: timeSlot.endHour,
                    endMin: timeSlot.endMin,
                  } as TimeSlot;
                  setTimeSlots(newTimeSlots);
                }}
              />
            </TimeSlotCell>
            <TimeSlotCell item xs={1}>
              to
            </TimeSlotCell>
            <TimeSlotCell item xs={4}>
              <TimeSlotDisplayTimeStamp
                hour={timeSlot.endHour}
                minutes={timeSlot.endMin}
                onChange={(hour, minutes) => {
                  const newTimeSlots = [...timeSlots];
                  newTimeSlots[index] = {
                    startHour: timeSlot.startHour,
                    startMin: timeSlot.startMin,
                    endHour: hour,
                    endMin: minutes,
                  } as TimeSlot;
                  setTimeSlots(newTimeSlots);
                }}
              />
            </TimeSlotCell>
            <TimeSlotCell item xs={3}>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <TimeSlotButton
                  onClick={() => {
                    const newTimeSlots = [...timeSlots];
                    newTimeSlots.splice(index, 1);
                    setTimeSlots(newTimeSlots);
                  }}
                  tooltip={"Remove this time"}
                  icon={<RemoveIcon />}
                />
                {index === timeSlots.length - 1 && (
                  <TimeSlotButton
                    onClick={() => {
                      setTimeSlots([...timeSlots, initialTimeSlot()]);
                    }}
                    tooltip={"Add new time"}
                    icon={<AddCircleIcon size={20} />}
                  />
                )}
                {showCopy && index === 0 && (
                  <TimeSlotButton
                    onClick={onCopyClick}
                    tooltip={"Copy to all"}
                    icon={<DuplicateIcon size={20} />}
                  />
                )}
              </Box>
            </TimeSlotCell>
            {!isValidTimeRange(timeSlot) && (
              <Hint
                sx={{ marginTop: 1 }}
                hintValue="Invalid time range. Must be between 4:30am and 4:00am the next day."
                severity="error"
              />
            )}
          </Grid>
        );
      })}
    </Grid>
  </OpeningHoursRow>
);

const TimeSlotCell = styled(Grid)(() => ({
  display: "block",
  alignItems: "center",
  textAlign: "center",
}));

const OpeningHoursRow = styled(Stack)(() => ({
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  flexWrap: "wrap",
}));

const selectableTimes = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? 0 : 30;
  return {
    label: `${hour}:${minutes === 0 ? "00" : minutes}`,
    value: i,
  };
});

const IgfdInput = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    minWidth: "40px",
    borderRadius: 8,
    textAlign: "left",
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey.A200}`,
    fontSize: theme.typography.body2.fontSize,
    padding: "8px 16px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    fontFamily: theme.typography.body2.fontFamily,
    "&:focus": {
      borderColor: theme.palette.secondary.main,
      boxShadow: `${alpha(theme.palette.secondary.main, 0.1)} 0 0 0 0.2rem`,
    },
  },
}));

/**
 * Time Slot display and selector
 */
const TimeSlotDisplayTimeStamp = ({
  hour,
  minutes,
  onChange,
}: {
  hour: number;
  minutes: number;
  onChange: (hour: number, minutes: number) => void;
}) => {
  return (
    <Select
      onChange={(event) => {
        onChange(
          Math.floor((event.target.value as number) / 2),
          (event.target.value as number) % 2 === 0 ? 0 : 30
        );
      }}
      input={<IgfdInput />}
      MenuProps={{ PaperProps: { sx: { height: "50%" } } }}
      value={hour * 2 + (minutes === 0 ? 0 : 1)}
    >
      {selectableTimes.map((time) => {
        return (
          <MenuItem key={time.label} value={time.value}>
            {time.label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

/**
 * Time Slot button for adding and removing time slots
 */
const TimeSlotButton = ({
  onClick,
  tooltip,
  icon,
}: {
  onClick?: () => void;
  tooltip: string;
  icon: React.ReactNode;
}) => {
  return (
    <IconButton onClick={onClick} size="small">
      <Tooltip title={tooltip} placement="right">
        <>{icon}</>
      </Tooltip>
    </IconButton>
  );
};
